import type { ComponentType, ReactNode } from "react";
import { Link } from "react-router-dom";
import { CaretRight, type IconProps } from "@phosphor-icons/react";

/**
 * One line per thing, and the whole line is the link: the client's list, `prototype/kund/pages.js`
 * `rowLink()`, the second build round of 2026-09-24.
 *
 * A row is a type icon, the thing itself, what state it is in, and a caret. The state is a grey word
 * when it is normal and a badge only when it asks something of the client (design.md section 3, rule
 * 2), and the caller decides which by what it passes as `trail`.
 */
export function RowList({
  compact = false,
  label,
  className,
  testId,
  children
}: {
  compact?: boolean;
  label?: string;
  className?: string;
  testId?: string;
  children: ReactNode;
}) {
  const classes = ["k-rows", compact ? "is-compact" : "", className ?? ""].filter(Boolean).join(" ");

  return (
    <ul className={classes} aria-label={label} data-testid={testId}>
      {children}
    </ul>
  );
}

export type RowTone = "info" | "warn" | "danger";

export function RowLink({
  to,
  icon: Icon,
  tone,
  children,
  trail,
  current = false,
  onClick,
  testId,
  className
}: {
  to: string;
  icon?: ComponentType<IconProps>;
  tone?: RowTone;
  /** The thing itself: a label, or a `primary-cell` with a line under it. */
  children: ReactNode;
  trail?: ReactNode;
  current?: boolean;
  onClick?: () => void;
  testId?: string;
  className?: string;
}) {
  return (
    <li className={className}>
      <Link
        className={`k-row${current ? " is-current" : ""}`}
        to={to}
        onClick={onClick}
        aria-current={current ? "page" : undefined}
        data-testid={testId}
      >
        {Icon ? (
          <span className={`type-icon${tone ? ` tone-${tone}` : ""}`} aria-hidden="true">
            <Icon size={18} />
          </span>
        ) : null}
        <span className="k-row-main">{children}</span>
        {trail ? <span className="k-row-trail">{trail}</span> : null}
        <span className="k-row-go" aria-hidden="true">
          <CaretRight size={16} />
        </span>
      </Link>
    </li>
  );
}

/** A line in a list that is not a link: a person, a session, a request that is only read here. */
export function RowItem({
  icon: Icon,
  tone,
  children,
  trail,
  testId,
  className
}: {
  icon?: ComponentType<IconProps>;
  tone?: RowTone;
  children: ReactNode;
  trail?: ReactNode;
  testId?: string;
  className?: string;
}) {
  return (
    <li className={`k-item${className ? ` ${className}` : ""}`} data-testid={testId}>
      {Icon ? (
        <span className={`type-icon${tone ? ` tone-${tone}` : ""}`} aria-hidden="true">
          <Icon size={18} />
        </span>
      ) : null}
      <div className="k-row-main">{children}</div>
      {trail ? <div className="k-row-trail">{trail}</div> : null}
    </li>
  );
}
